import { useEffect, useState } from "react";
import { Box, Card, Typography, TextField, Alert } from "@mui/material";
import ReactApexChart from "react-apexcharts";
import { adminApi } from "rsa/adminApi";
import SectionHeader from "../Components/SectionHeader";

const cardSx = { borderRadius: "15px", boxShadow: "rgba(0,0,0,0.1) 0px 10px 15px -3px, rgba(0,0,0,0.05) 0px 4px 6px -2px", p: 2 };
const BRAND_COLORS = ["rgb(173,73,112)", "rgb(233,56,72)", "rgb(119,53,98)", "rgb(255,184,107)"];

function Tile({ label, value }) {
  return (
    <Card sx={{ ...cardSx, flex: "1 1 180px", textAlign: "center" }}>
      <Typography variant="h4" fontWeight={700}>{value}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
    </Card>
  );
}

export default function AnalyticsPage() {
  const [range, setRange] = useState({ from: "", to: "" });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      setError("");
      const qs = new URLSearchParams(Object.fromEntries(Object.entries(range).filter(([, v]) => v))).toString();
      setData(await adminApi("GET", `/admin/analytics${qs ? `?${qs}` : ""}`));
    } catch (e) { setError(e.status === 401 ? "Session expired — sign in again." : e.message); }
  };
  useEffect(() => { load(); }, [range]); // eslint-disable-line

  return (
    <Box>
      <SectionHeader
        title="Analytics"
        action={
          <Box display="flex" gap={1}>
            <TextField size="small" type="date" label="From" InputLabelProps={{ shrink: true }}
              onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
            <TextField size="small" type="date" label="To" InputLabelProps={{ shrink: true }}
              onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
          </Box>
        }
      />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {data && (
        <>
          <Box display="flex" gap={2} flexWrap="wrap" mb={2}>
            <Tile label="Total requests" value={data.totalRequests} />
            <Tile label="Completed" value={data.completedRequests} />
            <Tile label="Completion rate" value={`${Math.round(data.completionRate * 100)}%`} />
            <Tile label="Avg time to claim" value={data.avgTimeToClaimSeconds != null ? `${Math.round(data.avgTimeToClaimSeconds)}s` : "—"} />
          </Box>
          <Box display="flex" gap={2} flexWrap="wrap">
            <Card sx={{ ...cardSx, flex: "1 1 380px" }}>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>Volume by service type</Typography>
              <ReactApexChart
                type="bar" height={300}
                options={{
                  colors: BRAND_COLORS,
                  chart: { toolbar: { show: false } },
                  xaxis: { categories: data.volumeByServiceType.map((r) => r.service_type || "Unknown") },
                  plotOptions: { bar: { distributed: true, borderRadius: 6 } },
                  legend: { show: false },
                }}
                series={[{ name: "Requests", data: data.volumeByServiceType.map((r) => +r.volume) }]}
              />
            </Card>
            <Card sx={{ ...cardSx, flex: "1 1 380px" }}>
              <Typography variant="subtitle1" fontWeight={600} mb={1}>Volume by severity</Typography>
              <ReactApexChart
                type="bar" height={300}
                options={{
                  colors: BRAND_COLORS,
                  chart: { toolbar: { show: false } },
                  xaxis: { categories: data.volumeBySeverity.map((r) => r.severity) },
                  plotOptions: { bar: { distributed: true, borderRadius: 6 } },
                  legend: { show: false },
                }}
                series={[{ name: "Requests", data: data.volumeBySeverity.map((r) => +r.volume) }]}
              />
            </Card>
          </Box>
        </>
      )}
    </Box>
  );
}
